import { useMutation } from '@tanstack/react-query';
import { toast } from '@huspy/forge/shared';
import { useTranslation } from 'react-i18next';
import { bankApplicationsApi } from '@modules/opportunities/entities/bankApplication/api';
import queryClient from '@app/queryClient';
import { clearAmplitudeEventContext, trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';

type MutationFnPayload = {
  bankApplicationId: string;
  clientId?: string | null;
  bankId?: string;
  file: File;
};

const useUploadBankApplicationDocument = (opportunityId: string) => {
  const { t } = useTranslation();

  const {
    error, mutate, mutateAsync, isPending,
  } = useMutation({
    mutationKey: ['uploadBankApplicationDocument', opportunityId],
    mutationFn: ({
      file, bankApplicationId, clientId, bankId,
    }: MutationFnPayload) => {
      clearAmplitudeEventContext();

      // Track attempt - bankId used to define if it is v1/v2
      if (bankId) {
        trackAmplitudeEvent(USER_EVENTS.CASE.CASE_BANK_FORMS_UPLOAD_ATTEMPTED, {
          addToContext: true,
          case_id: opportunityId,
          digital_form_generated: 0,
          client_id: clientId,
          bank: bankId,
        });
      }

      const formData = new FormData();
      formData.append('document', file);
      return bankApplicationsApi.uploadDocument(
        opportunityId,
        bankApplicationId,
        formData
      );
    },
    onSuccess: (_, variables) => {
      // Track success
      if (variables.bankId) {
        trackAmplitudeEvent(USER_EVENTS.CASE.CASE_BANK_FORMS_UPLOAD_COMPLETED, { useContext: true });
      }

      queryClient.refetchQueries({ queryKey: ['clientOpportunity', opportunityId] });
    },
    onError: (data, variables) => {
      // Track fail
      if (variables.bankId) {
        trackAmplitudeEvent(USER_EVENTS.CASE.CASE_BANK_FORMS_UPLOAD_FAILED, {
          useContext: true,
          error_message: data.message ?? data,
        });
      }

      const defaultMessage = t('opportunity.caseSubmission.documentSubmission.notifications.documentUploadFailed');
      toast('error', { message: data.message || defaultMessage });
    },
  });

  return {
    mutate, mutateAsync, error, isPending,
  };
};

export default useUploadBankApplicationDocument;
